import { useEffect } from "react";

/**
 * SeoMeta — A minimal, React-19-safe replacement for react-helmet-async on
 * the public-facing pages where we actually need per-route <head> mutation
 * (BusinessProfilePage, DirectoryPage hero, etc.).
 *
 * react-helmet-async@2.0.5 silently fails to inject most meta tags on
 * React 19, only managing <title>. This module bypasses the library
 * entirely by writing to document.head with a tag-id attribute so we can
 * safely upsert/remove our own tags without disturbing the static ones.
 *
 * Usage:
 *   <SeoMeta
 *     title="..."
 *     description="..."
 *     canonical="..."
 *     ogImage="..."
 *     ogType="business.business"
 *     twitterCard="summary_large_image"
 *     extraMeta={[{ name: 'twitter:image', content: '...' }, ...]}
 *     jsonLd={[localBusinessSchema, breadcrumbSchema]}
 *   />
 */
const TAG_ATTR = "data-localbest-seo";

function setMeta(key, content, attrName = "property") {
  if (content === undefined || content === null || content === "") return null;
  // Find an existing tag we own
  const owned = document.head.querySelector(`meta[${TAG_ATTR}="${key}"]`);
  if (owned) {
    owned.setAttribute("content", String(content));
    return owned;
  }
  // Or upsert one matching the legacy attribute (replace static index.html tags)
  const existing = document.head.querySelector(`meta[${attrName}="${key}"]`);
  const tag = existing || document.createElement("meta");
  tag.setAttribute(attrName, key);
  tag.setAttribute("content", String(content));
  tag.setAttribute(TAG_ATTR, key);
  if (!existing) document.head.appendChild(tag);
  return tag;
}

function setLinkRel(rel, href) {
  if (!href) return null;
  const key = `link:${rel}`;
  const owned = document.head.querySelector(`link[${TAG_ATTR}="${key}"]`);
  if (owned) { owned.setAttribute("href", href); return owned; }
  const existing = document.head.querySelector(`link[rel="${rel}"]`);
  const tag = existing || document.createElement("link");
  tag.setAttribute("rel", rel);
  tag.setAttribute("href", href);
  tag.setAttribute(TAG_ATTR, key);
  if (!existing) document.head.appendChild(tag);
  return tag;
}

function setJsonLdBlock(index, data) {
  const key = `jsonld:${index}`;
  let script = document.head.querySelector(`script[${TAG_ATTR}="${key}"]`);
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(TAG_ATTR, key);
    document.head.appendChild(script);
  }
  try {
    script.textContent = JSON.stringify(data);
  } catch (_) {
    script.textContent = "{}";
  }
  return script;
}

export default function SeoMeta({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType,
  ogUrl,
  ogSiteName,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  extraMeta,
  jsonLd,
}) {
  useEffect(() => {
    const touched = [];

    if (title) {
      document.title = title;
    }

    const m = (k, v, attr = "property") => { const t = setMeta(k, v, attr); if (t) touched.push(t); };

    m("description", description, "name");

    // Open Graph
    m("og:type", ogType || "website");
    m("og:title", ogTitle || title);
    // For og:description: undefined → fall back to SEO description.
    // Explicit "" or null → suppress (no tag).
    if (ogDescription !== "" && ogDescription !== null) {
      m("og:description", ogDescription || description);
    }
    m("og:image", ogImage);
    m("og:url", ogUrl || canonical);
    m("og:site_name", ogSiteName);
    if (ogImage) {
      m("og:image:width", "1200");
      m("og:image:height", "630");
    }

    // Twitter
    m("twitter:card", twitterCard || "summary_large_image", "name");
    m("twitter:title", twitterTitle || ogTitle || title, "name");
    if (twitterDescription !== "" && twitterDescription !== null
        && (ogDescription !== "" && ogDescription !== null)) {
      m("twitter:description", twitterDescription || ogDescription || description, "name");
    }
    m("twitter:image", twitterImage || ogImage, "name");

    // Canonical
    if (canonical) {
      const link = setLinkRel("canonical", canonical);
      if (link) touched.push(link);
    }

    // Extra meta (geo, business:contact_data:*, keywords, robots, etc.)
    if (Array.isArray(extraMeta)) {
      for (const entry of extraMeta) {
        if (!entry || !entry.content) continue;
        const attr = entry.name ? "name" : "property";
        const key = entry.name || entry.property;
        m(key, entry.content, attr);
      }
    }

    // JSON-LD blocks — isolated so a bad payload can't kill meta injection
    let scripts = [];
    if (Array.isArray(jsonLd)) {
      jsonLd.forEach((block, i) => {
        if (block && typeof block === "object") {
          scripts.push(setJsonLdBlock(i, block));
        }
      });
    }

    return () => {
      // Cleanup on unmount — remove only the tags we owned.
      const owned = document.head.querySelectorAll(`[${TAG_ATTR}]`);
      owned.forEach((node) => {
        // Don't remove meta tags that were also legacy index.html tags
        // (we left them in place when upserting); just clear the marker.
        if (node.tagName === "SCRIPT" || node.hasAttribute(TAG_ATTR)) {
          // For meta/link, only remove if we appended (no other dups exist).
          // Simplest: remove our owned scripts; leave meta/links so subsequent
          // pages overwrite, not delete.
          if (node.tagName === "SCRIPT") {
            node.parentNode && node.parentNode.removeChild(node);
          }
        }
      });
    };
  }, [
    title, description, canonical,
    ogTitle, ogDescription, ogImage, ogType, ogUrl, ogSiteName,
    twitterCard, twitterTitle, twitterDescription, twitterImage,
    JSON.stringify(extraMeta), JSON.stringify(jsonLd),
  ]);

  return null;
}
